<template>
    <div class="dm-auth-wrapper">
        <div class="d-flex flex-sm-column justify-content-center">
            <div class="card shadow-sm bg-white">
                <div class="card-body">
                    <h4 class="text-center text-dark">
                        {{ translate("Create an account with bb-flow") }}
                    </h4>
                    <form
                        class="dm-parsley-validate-form"
                        id="dm-create-author-account-form"
                        @submit.prevent="validateAndSubmit()"
                    >
                        <div class="mt-2">
                            <label class="form-label" for="first_name">
                                {{ translate("First Name") }}
                                <span class="required">*</span>
                            </label>
                            <input
                                class="form-control"
                                id="first_name"
                                type="text"
                                name="first_name"
                                data-parsley-name-with-diacritics-and-apostrophes
                                required="required"
                                autofocus="autofocus"
                                v-model="author.first_name"
                            />
                        </div>

                        <div class="mt-4">
                            <label class="form-label" for="last_name">
                                {{ translate("Last Name") }}
                                <span class="required">*</span>
                            </label>
                            <input
                                class="form-control"
                                id="last_name"
                                type="text"
                                name="last_name"
                                data-parsley-name-with-diacritics-and-apostrophes
                                required="required"
                                v-model="author.last_name"
                            />
                        </div>

                        <div class="mt-4">
                            <label class="form-label" for="email">
                                {{ translate("Email") }}
                                <span class="required">*</span>
                            </label>
                            <input
                                class="form-control"
                                :class="{
                                    'is-invalid': email_is_unique == 'no',
                                }"
                                id="email"
                                type="email"
                                name="email"
                                required="required"
                                v-model="author.email"
                                @change="checkEmailIsUnique()"
                                @keyup="email_is_unique = 'unchecked'"
                            />

                            <ul
                                class="parsley-errors-list filled"
                                v-if="email_is_unique == 'no'"
                            >
                                <li class="parsley-minlength">
                                    {{ translate(  "Have you got an account already? The email address you entered is already in use." ) }}
                                    <br />
                                    {{ translate( 'Click "Already registered?" below' ) }}
                                </li>
                            </ul>
                        </div>

                        <div class="mt-4">
                            <label class="form-label" for="phone">
                                {{ translate("Phone") }}
                            </label>
                            <vue-tel-input
                                v-model="author.phone"
                                mode="international"
                                class="dm-tel-input-wrap"
                            ></vue-tel-input>
                        </div>

                        <div class="mt-4">
                            <label class="form-label" for="country">
                                {{ translate("Country") }}
                                <span class="required">*</span>
                            </label>
                            <select
                                id="country"
                                class="form-control"
                                type="text"
                                name="country"
                                required=""
                                v-model="author.country"
                            >
                                <option>
                                    {{ translate("Select Country") }}
                                </option>
                                <option
                                    v-for="(
                                        countryName, countryISO
                                    ) in countriesList()"
                                    :value="countryISO"
                                    :key="'country-' + countryISO"
                                >
                                    {{ translate(countryName) }}
                                </option>
                            </select>
                        </div>

                        <div class="mt-4">
                            <label class="form-label" for="password">
                                {{ translate("Password") }}
                                <span class="required">*</span>
                            </label>
                            <input
                                class="form-control"
                                id="password"
                                type="password"
                                name="password"
                                data-parsley-minlength="8"
                                required="required"
                                autocomplete="new-password"
                                v-model="author.password"
                            />
                        </div>

                        <div class="mt-4">
                            <label
                                class="form-label"
                                for="password_confirmation"
                            >
                                {{ translate("Confirm Password") }}
                                <span class="required">*</span>
                            </label>
                            <input
                                class="form-control"
                                id="password_confirmation"
                                type="password"
                                name="password_confirmation"
                                data-parsley-minlength="8"
                                data-parsley-equalto="#password"
                                required="required"
                                v-model="author.password_confirmation"
                            />
                        </div>
                        <div class="mt-4 checkbox-list">
                            <label for="terms-checkbox" class="checkbox mb-0">
                                <input
                                    id="terms-checkbox"
                                    class=""
                                    type="checkbox"
                                    name="terms-checkbox"
                                    required=""
                                    v-model="author.agree_terms"
                                />
                                <span></span>
                                {{ translate("I agree to bb-flow's") }}
                                <a
                                    href="/terms"
                                    target="_blank"
                                    class="ml-2 text-decoration-underline"
                                    >{{ translate("Terms and Conditions") }}</a
                                >
                            </label>
                        </div>
                        <div class="mt-0 checkbox-list">
                            <label
                                for="privacy-policy-checkbox"
                                class="checkbox mb-0"
                            >
                                <input
                                    id="privacy-policy-checkbox"
                                    class=""
                                    type="checkbox"
                                    name="privacy-policy-checkbox"
                                    required=""
                                    v-model="author.agree_privacy_policy"
                                />
                                <span></span>
                                {{ translate("I agree to bb-flow's") }}
                                <a
                                    href="/privacy-policy"
                                    target="_blank"
                                    class="ml-2 text-decoration-underline"
                                    >{{ translate("Privacy Policy") }}</a
                                >
                            </label>
                        </div>

                        <div class="mt-4 mb-2">
                            <vue-recaptcha
                                :sitekey="recaptchaSiteKey"
                                @verify="verifyMethod"
                                @expired="expiredMethod"
                                @render="expiredMethod"
                                @error="errorMethod"
                            ></vue-recaptcha>
                        </div>

                        <div
                            v-if="errors"
                            class="alert alert-danger mt-4"
                        >
                            <ul class="mb-0 pl-3">
                                <template v-for="(messages, field) in errors">
                                    <li v-for="message in messages" :key="field + message">{{ message }}</li>
                                </template>
                            </ul>
                        </div>

                        <div
                            class="d-flex align-content-center justify-content-between mt-4"
                        >
                            <a
                                class="text-dark d-flex align-content-center mt-3 text-decoration-underline"
                                v-if="emit_switch_to_sign_up"
                                href=""
                                @click.prevent="$emit('switchForm')"
                                data-cy="dm-landing-already-registered-link"
                                >{{ translate("Already registered") }}?</a
                            >

                            <a
                                class="text-dark d-flex align-content-center mt-3 text-decoration-underline"
                                v-else
                                :href="loginUrl"
                            >
                                {{ translate("Already registered?") }}
                            </a>

                            <button
                                type="submit"
                                class="btn dm-btn-brand font-weight-bolder ml-4"
                                data-cy="dm-landing-register-author-button"
                                :disabled="registering"
                                v-html="registerButtonHTML"
                            ></button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { VueRecaptcha } from 'vue-recaptcha';
import { markRaw } from 'vue';

export default {
    props: ["emit_switch_to_sign_up"],

    components: { VueRecaptcha },

    data() {
        return {
            recaptchaSiteKey: recaptchaSiteKey, // Must exist in DOM
            author: {
                first_name: "",
                last_name: "",
                email: "",
                phone: "",
                country: "",
                password: "",
                password_confirmation: "",
                agree_terms: "",
                agree_privacy_policy: "",
                company_id: companyId, // NB: Must be defined in the page where the form is used
                entry_point_id: typeof entryPointId !== 'undefined' ? entryPointId : null, // NB: Must be defined in the page where the form is used
                recaptcha_response: "",
            },
            errors: null,

            validatedForm: null,
            email_is_unique: "unchecked",
            parsleyOptions: {
                errorClass: "is-invalid",
                successClass: "is-valid",
            },
            registering: false,
            hasValidated: false,
        };
    },

    computed: {
        registerButtonHTML() {
            return this.registering
                ? this.translate("Registering") +
                      '<span class="spinner spinner-track spinner-info spinner-lg ml-4 mr-6"></span>'
                : this.translate("Register");
        },

        loginUrl() {
            if (typeof landingPageId != undefined)
                return "/login?redirect_to_landing_page_id=" + landingPageId;

            return "/login";
        },
    },

    methods: {
        verifyMethod(response) {
            // console.log('verifyMethod');
            // console.log(response);
            this.author.recaptcha_response = response;
            this.errors = null;
        },

        expiredMethod(response) {
            // console.log('expiredMethod');
            // console.log(response);
        },

        errorMethod(response) {
            // console.log('errorMethod');
            // console.log(response);
        },


        validateAndSubmit() {
            // Prevent recursion
            if (this.hasValidated || this.registering) {
                return false;
            }

            // Check if Parsley is initialized
            if (!this.validatedForm) {
                console.error('Parsley form not initialized');
                return false;
            }

            // Mark that we've validated to prevent re-entry
            this.hasValidated = true;

            // Use whenValidate() for proper async validation without triggering submit
            this.validatedForm.whenValidate()
                .then(() => {
                    // Validation passed
                    this.checkEmailIsUniqueAgainAndSubmit();
                })
                .catch(() => {
                    // Validation failed, errors already shown by Parsley
                    this.hasValidated = false; // Reset so user can try again
                });

            return false; // Prevent default form submission
        },

        checkEmailIsUniqueAgainAndSubmit() {
            var self = this;
            self.email_is_unique = "checking";

            axios
                .post("/api/is-email-unique", { email: this.author.email })
                .then(function (response) {
                    self.email_is_unique = response.data;

                    if (self.email_is_unique == "yes") {
                        self.submitForm();
                    } else {
                        // Reset validation flag so user can try again
                        self.hasValidated = false;
                        self.$swal({
                            title: self.translate("Email already exists"),
                            text: self.translate(
                                "The email you entered already exists. Either log in or add a different email.",
                            ),
                            icon: "info",
                            confirmButtonText: self.translate("Ok"),
                            customClass: {
                                confirmButton: "btn btn-light-success",
                            },
                        });
                    }
                });
        },

        submitForm() {
            this.registering = true;
            this.errors = null;

            var self = this;
            axios
                .post("/api/author/register", this.author)
                .then(function (response) {
                    self.$swal({
                        title: self.translate("Account Created"),
                        html:
                            self.translate(
                                "Your author account was created successfuly.",
                            ) +
                            "<br /><br />" +
                            self.translate(
                                "Before you can submit, we need you to verify your email address. Please follow the instructions in the email we just sent you. If you did not receive an email from us, please check that you registered with the correct email.",
                            ),
                        icon: "success",
                    }).then((result) => {
                        window.location.href = "/author";
                    });
                })
                .catch(function (error) {
                    self.registering = false;
                    self.hasValidated = false; // Reset so user can try again
                    if (error.response && error.response.data && error.response.data.errors) {
                        self.errors = error.response.data.errors;
                    }
                });
        },

        checkEmailIsUnique() {
            // Used @change
            var self = this;
            self.email_is_unique = "checking";

            axios
                .post("/api/is-email-unique", { email: this.author.email })
                .then(function (response) {
                    self.email_is_unique = response.data;
                });
        },
    },

    mounted() {
        // TODO: Share this. Add this validator rule to the field: data-parsley-name-with-diacritics-and-apostrophes
        window.Parsley.addValidator("nameWithDiacriticsAndApostrophes", {
            validateString: function (value) {
                const regex = /^\p{L}[\p{L}0-9'’\- ]{1,49}$/u; // your regex with Unicode flag
                return regex.test(value);
            },
            messages: {
                en: "This value should be a valid name.",
            },
        });

        // Use markRaw to prevent Vue from making Parsley object reactive
        // Parsley objects have circular references which cause infinite loops in Vue's reactivity
        this.validatedForm = markRaw(
            jQuery("#dm-create-author-account-form").parsley(this.parsleyOptions)
        );
    },
};
</script>
